import React from "react";
import { SchemaModel, SourceItemModel } from "@/models/schema";
import {
  TrackersItemSchemaModel,
  ParameterSchemaModel,
  TrackerSchemaModel,
} from "@/models/tracker";

/**
 * Interface pour représenter la structure OpenAPI
 */
interface OpenAPISpec {
  openapi: string;
  info: {
    title: string;
    description: string;
    version: string;
  };
  paths: Record<string, any>;
  components: {
    schemas: Record<string, any>;
  };
  tags?: Array<{
    name: string;
    description: string;
    externalDocs?: {
      description: string;
      url: string;
    };
  }>;
}

/**
 * Parse le fichier OpenAPI pour extraire les sources et trackers
 */
export class OpenAPIParser {
  private openApiSpec: OpenAPISpec;

  constructor(openApiSpec: OpenAPISpec) {
    this.openApiSpec = openApiSpec;
  }

  /**
   * Extrait les sources et trackers du schéma OpenAPI
   */
  parseToSchemaModel(): SchemaModel {
    const sources = this.extractSources();
    const documents = this.extractDocumentsFromSchemas();

    return {
      sources,
      documents,
      normalizedModel: {},
    };
  }

  /**
   * Vérifie si une source a au moins un endpoint avec x-tracker: true
   */
  private sourceHasTrackerEndpoints(sourceName: string): boolean {
    const paths = this.openApiSpec.paths || {};
    const normalizedSource = sourceName.toLowerCase();

    for (const [path, pathItem] of Object.entries(paths)) {
      // Vérifier si le path commence par cette source (ex: /tiktok/... pour source "tiktok")
      // On extrait le premier segment du path après le /
      const pathSegments = path.split("/").filter(Boolean);
      const pathSource = pathSegments[0]?.toLowerCase();
      if (pathSource !== normalizedSource) {
        continue;
      }

      // Vérifier si au moins une opération a x-tracker: true
      for (const [, operation] of Object.entries(pathItem)) {
        if (
          operation &&
          typeof operation === "object" &&
          "x-tracker" in operation &&
          (operation as any)["x-tracker"] === true
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Extrait les sources du schéma OpenAPI depuis les tags
   * Ne garde que les sources qui ont au moins un endpoint avec x-tracker: true
   */
  private extractSources(): Record<string, SourceItemModel> {
    const sources: Record<string, SourceItemModel> = {};

    // Extraire les sources depuis les tags OpenAPI
    const tags = this.openApiSpec.tags || [];

    for (const tag of tags) {
      const sourceName = this.normalizeSourceName(tag.name);
      // Ne garder que les sources qui ont au moins un endpoint x-tracker
      if (!this.sourceHasTrackerEndpoints(sourceName)) {
        continue;
      }
      sources[sourceName] = this.parseSourceFromTag(tag);
    }

    return sources;
  }

  /**
   * Extrait les documents du schéma OpenAPI depuis les composants
   */
  private extractDocumentsFromSchemas(): Record<string, any> {
    const documents: Record<string, any> = {};

    const schemas = this.openApiSpec.components?.schemas || {};

    for (const [schemaName, schema] of Object.entries(schemas)) {
      if (this.isDocumentSchema(schema)) {
        const docName = this.extractDocumentName(schemaName);
        if (docName) {
          documents[docName] = this.parseDocumentSchema(schema);
        }
      }
    }

    return documents;
  }

  /**
   * Normalise le nom de la source depuis le tag
   */
  private normalizeSourceName(tagName: string): string {
    return tagName.toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  /**
   * Parse une source depuis un tag OpenAPI
   */
  private parseSourceFromTag(tag: any): SourceItemModel {
    const { logo, logoFallback } = this.extractLogoFromTag(tag);
    return {
      metadata: {
        name: tag.name,
        description: tag.description || "",
        logo,
        logoFallback,
      },
      trackers: this.extractTrackersForSource(tag.name),
    };
  }

  /**
   * Extrait le logo depuis les métadonnées du tag
   * Simple Icons par défaut, métadonnées de l'API en fallback
   * Retourne { logo, logoFallback }
   */
  private extractLogoFromTag(tag: any): {
    logo: string | React.ReactNode | undefined;
    logoFallback: string | React.ReactNode | undefined;
  } {
    // Par défaut: essayer de récupérer le logo depuis Simple Icons
    const sourceName = this.normalizeSourceName(tag.name);
    const simpleIconLogo = this.getSimpleIconLogo(sourceName);

    // Extraire le logo de l'API pour le fallback
    let apiLogo: React.ReactNode | undefined;
    if (tag.logo) {
      apiLogo = this.parseLogoFromMetadata(tag.logo);
    } else if (tag.externalDocs?.logo) {
      apiLogo = this.parseLogoFromMetadata(tag.externalDocs.logo);
    } else if (tag["x-logo"]) {
      apiLogo = this.parseLogoFromMetadata(tag["x-logo"]);
    }

    return {
      logo: simpleIconLogo || apiLogo || undefined,
      logoFallback: apiLogo || undefined,
    };
  }

  /**
   * Parse le logo depuis les métadonnées
   */
  private parseLogoFromMetadata(logoData: any): React.ReactNode {
    // Si c'est déjà un composant React
    if (React.isValidElement(logoData)) {
      return logoData;
    }

    // Si c'est une URL d'image valide
    if (
      typeof logoData === "string" &&
      (logoData.startsWith("http") || logoData.startsWith("/"))
    ) {
      return logoData;
    }

    // Si c'est une chaîne qui n'est pas une URL valide, retourner undefined
    // pour éviter les erreurs Next.js Image avec des emojis ou autres chaînes
    if (typeof logoData === "string") {
      return undefined;
    }

    // Si c'est un objet avec des propriétés, retourner l'objet
    if (typeof logoData === "object" && logoData.type) {
      return logoData;
    }

    // Pas de fallback hardcodé
    return undefined;
  }

  /**
   * Récupère le logo depuis Simple Icons pour une source donnée
   * Utilise Simple Icons (un service populaire pour les logos de marques)
   * Génère dynamiquement l'URL basée sur le nom normalisé de la source
   */
  private getSimpleIconLogo(sourceName: string): string | undefined {
    // Normaliser le nom de la source pour correspondre au format Simple Icons
    // Simple Icons utilise des noms en minuscules, sans espaces ni caractères spéciaux
    let normalizedName = sourceName.toLowerCase().trim();

    // Remplacer les caractères spéciaux et espaces par rien
    normalizedName = normalizedName.replace(/[^a-z0-9]/g, "");

    // Dictionnaire de cas spéciaux pour les sources dont le nom diffère dans Simple Icons
    const specialCases: Record<string, string> = {
      vkontakte: "vk",
      weibo: "sinaweibo",
      okru: "odnoklassniki",
      press: "newspaper",
      news: "newspaper",
    };

    // Vérifier si le nom normalisé correspond à un cas spécial
    if (specialCases[normalizedName]) {
      normalizedName = specialCases[normalizedName];
    }

    // Si le nom normalisé est vide ou trop court, ne pas essayer
    if (!normalizedName || normalizedName.length < 2) {
      return undefined;
    }

    // Générer dynamiquement l'URL Simple Icons via jsDelivr CDN
    // Format: https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/{icon-name}.svg
    // Note: L'URL sera générée même si l'icône n'existe pas dans Simple Icons
    // Le navigateur gérera l'erreur 404 si l'icône n'existe pas
    return `https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/${normalizedName}.svg`;
  }

  /**
   * Extrait les trackers pour une source donnée
   */
  private extractTrackersForSource(
    sourceName: string,
  ): TrackersItemSchemaModel[] {
    // Chercher dans les paths OpenAPI pour trouver les trackers de cette source
    const paths = this.openApiSpec.paths || {};
    const trackers: TrackersItemSchemaModel[] = [];

    for (const [path, pathItem] of Object.entries(paths)) {
      if (this.isTrackerPathForSource(path, sourceName)) {
        const tracker = this.parseTrackerFromPath(path, pathItem);
        if (tracker) {
          trackers.push(tracker);
        }
      }
    }

    return trackers;
  }

  /**
   * Vérifie si un path correspond à un tracker pour la source donnée
   * En mode normal, ne garde que les endpoints qui ont l'extension x-tracker = true
   * En mode avancé, affiche tous les endpoints qui correspondent à la source
   * Exclut toujours les endpoints qui ont x-odm = true
   */
  private isTrackerPathForSource(path: string, sourceName: string): boolean {
    // Vérifier si le path commence par cette source (ex: /tiktok/... pour source "tiktok")
    const normalizedSource = sourceName.toLowerCase();
    const pathSegments = path.split("/").filter(Boolean);
    const pathSource = pathSegments[0]?.toLowerCase();

    if (pathSource !== normalizedSource) {
      return false;
    }

    // Vérifier si l'endpoint a l'extension x-tracker
    const paths = this.openApiSpec.paths || {};
    const pathItem = paths[path];

    if (!pathItem) {
      return false;
    }

    // Vérifier dans toutes les opérations (GET, POST, PUT, DELETE, etc.)
    // Exclure les endpoints qui ont x-odm = true
    let hasValidOperation = false;
    for (const [method, operation] of Object.entries(pathItem)) {
      if (operation && typeof operation === "object") {
        // Ignorer les endpoints avec x-odm = true
        if ("x-odm" in operation && (operation as any)["x-odm"] === true) {
          continue; // Ignorer cette opération
        }

        // Si x-tracker existe et est true, l'inclure (mode normal)
        if (
          "x-tracker" in operation &&
          (operation as any)["x-tracker"] === true
        ) {
          hasValidOperation = true;
          break;
        }
        // Si x-tracker n'existe pas ou est false, l'inclure aussi (mode avancé)
        // Cela permet d'afficher tous les endpoints en mode avancé
        if (
          !("x-tracker" in operation) ||
          (operation as any)["x-tracker"] === false
        ) {
          hasValidOperation = true;
          break;
        }
      }
    }

    return hasValidOperation;
  }

  /**
   * Parse un tracker depuis un path OpenAPI
   * Utilise les informations de l'extension x-tracker si disponible
   */
  private parseTrackerFromPath(
    path: string,
    pathItem: any,
  ): TrackersItemSchemaModel | null {
    // Extraire le nom du tracker depuis le path
    const pathParts = path.split("/").filter(Boolean);

    // Chercher les opérations du path pour extraire le meilleur nom
    const operations = Object.values(pathItem).filter(
      (op) => op && typeof op === "object",
    );

    // Vérifier si au moins une opération a x-tracker === true
    let hasXTracker = false;
    for (const operation of operations) {
      if (
        operation &&
        typeof operation === "object" &&
        "x-tracker" in operation &&
        (operation as any)["x-tracker"] === true
      ) {
        hasXTracker = true;
        break;
      }
    }

    // Essayer d'extraire un nom plus descriptif depuis les opérations
    let trackerName = pathParts[pathParts.length - 1] || "default";

    // Chercher dans les opérations pour un summary ou title plus descriptif
    for (const operation of operations) {
      if (
        operation &&
        typeof operation === "object" &&
        "summary" in operation
      ) {
        const summary = (operation as any).summary;
        if (summary && typeof summary === "string") {
          trackerName = summary;
          break;
        }
      } else if (
        operation &&
        typeof operation === "object" &&
        "title" in operation
      ) {
        const title = (operation as any).title;
        if (title && typeof title === "string") {
          trackerName = title;
          break;
        }
      } else if (
        operation &&
        typeof operation === "object" &&
        "description" in operation
      ) {
        // Utiliser la description si elle est plus courte et descriptive
        const description = (operation as any).description;
        if (
          description &&
          typeof description === "string" &&
          description.length < 50 &&
          description.length > trackerName.length
        ) {
          trackerName = description;
        }
      }
    }

    // Extraire la description depuis les opérations
    let trackerDescription = "";
    for (const operation of operations) {
      if (
        operation &&
        typeof operation === "object" &&
        "description" in operation
      ) {
        const description = (operation as any).description;
        if (description && typeof description === "string") {
          trackerDescription = description;
          break;
        }
      }
    }

    const parameters = this.extractParametersFromOperations(operations);

    return {
      is_reponse_array: true, // Par défaut, les trackers retournent des tableaux
      tracker: {
        name: trackerName,
        path: path,
        renderer: `${pathParts[0]}-renderer`,
        is_tracker: true,
        parameters,
        description: trackerDescription || undefined,
        has_x_tracker: hasXTracker,
      },
    };
  }

  /**
   * Resolve a schema that may contain a $ref to components/schemas.
   */
  private resolveSchema(schema: any): any {
    if (!schema) return schema;
    if (schema.$ref) {
      const refName = schema.$ref.split("/").pop();
      if (refName) {
        const resolved = this.openApiSpec.components?.schemas?.[refName];
        if (resolved) return resolved;
      }
    }
    return schema;
  }

  /**
   * Extrait les paramètres depuis les opérations d'un path.
   * Les paramètres query/path (ajoutés par le gateway) reçoivent origin "gateway",
   * les paramètres du request body (propres au scraper) reçoivent origin "endpoint".
   */
  private extractParametersFromOperations(
    operations: any[],
  ): ParameterSchemaModel[] {
    const parameters: ParameterSchemaModel[] = [];
    const seen = new Set<string>();

    for (const operation of operations) {
      // 1. Request body parameters (endpoint-specific: user_id, query, etc.)
      const bodySchema = this.resolveSchema(
        operation.requestBody?.content?.["application/json"]?.schema,
      );
      if (bodySchema?.properties) {
        const requiredFields: string[] = bodySchema.required || [];
        for (const [name, prop] of Object.entries<any>(bodySchema.properties)) {
          if (seen.has(name)) continue;
          seen.add(name);

          const resolvedProp = this.resolveSchema(prop);
          let propType = resolvedProp?.type || "string";
          if (!resolvedProp?.type && resolvedProp?.anyOf) {
            const nonNull = resolvedProp.anyOf.find(
              (s: any) => s.type && s.type !== "null",
            );
            if (nonNull) propType = nonNull.type;
          }

          parameters.push({
            required: requiredFields.includes(name),
            name,
            origin: "endpoint",
            example: resolvedProp?.examples?.[0] || resolvedProp?.example || "",
            schema: {
              title: resolvedProp?.title || resolvedProp?.description || name,
              type: propType,
              enum_description: {},
              items: resolvedProp?.items || {},
              suggest_endpoint: resolvedProp?.suggest_endpoint || "",
              default: resolvedProp?.default,
              light: resolvedProp?.light,
            },
          });
        }
      }

      // 2. Query/path/header parameters (gateway-level: response_format, limit, etc.)
      if (operation.parameters) {
        for (const param of operation.parameters) {
          if (seen.has(param.name)) continue;
          seen.add(param.name);
          parameters.push({
            required: param.required || false,
            name: param.name,
            origin: "gateway",
            example: param.example || "",
            schema: {
              title: param.description || param.name,
              type: param.schema?.type || "string",
              enum_description: {},
              items: {},
              suggest_endpoint: param.suggest_endpoint || "",
              default: param.schema?.default,
              light: param.schema?.light,
            },
          });
        }
      }
    }

    return parameters;
  }

  /**
   * Vérifie si un schéma représente un document
   */
  private isDocumentSchema(schema: any): boolean {
    return (
      schema?.properties?.title &&
      schema?.properties?.type &&
      schema?.properties?.doc_type &&
      schema?.type === "object"
    );
  }

  /**
   * Extrait le nom du document à partir du nom du schéma
   */
  private extractDocumentName(schemaName: string): string | null {
    // Logique pour extraire le nom du document
    const match = schemaName.match(/(\w+)Document$/i);
    return match ? match[1].toLowerCase() : null;
  }

  /**
   * Parse un schéma de document
   */
  private parseDocumentSchema(schema: any): any {
    return {
      title: schema.properties?.title?.title || "",
      type: schema.properties?.type?.title || "",
      description: schema.properties?.description?.title || "",
      enum: schema.properties?.enum?.items?.enum || [],
      properties: schema.properties?.properties || {},
      doc_type: schema.properties?.doc_type?.title || "",
    };
  }
}

/**
 * Fonction utilitaire pour parser un fichier OpenAPI
 */
export function parseOpenAPIToSchema(openApiSpec: OpenAPISpec): SchemaModel {
  const parser = new OpenAPIParser(openApiSpec);
  return parser.parseToSchemaModel();
}

/**
 * Hook pour utiliser le parser OpenAPI
 */
export function useOpenAPIParser(openApiSpec: OpenAPISpec | null) {
  if (!openApiSpec) return null;

  return parseOpenAPIToSchema(openApiSpec);
}
